import type { MOVolunteerPayload, ManageMOVolunteerResult } from './moVolunteerService';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
interface PendingOperation { version: 1; operationKey: string; payloadDigest: string; }
const storageKey = (actor: string, edition: string): string =>
  `rh.mo-volunteer.v1:${encodeURIComponent(actor)}:${encodeURIComponent(edition)}`;
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
/** The browser persists only an opaque operation key and digest, never profile
 * data. A lost response/reload cannot silently allocate a second runner.
 * This protocol must be deployed only together with the strict backend.
 */
export async function prepareVolunteerOperation(payload: MOVolunteerPayload, actorId: string,
  createPerson: boolean, storage: StorageLike = sessionStorage,
  cryptoApi: Pick<Crypto, 'subtle' | 'randomUUID'> = crypto): Promise<MOVolunteerPayload> {
  if (!nonempty(actorId) || !nonempty(payload.editionId) || !nonempty(payload.firstName) || !nonempty(payload.lastName)
    || !Number.isSafeInteger(payload.editionYear)) throw new Error('Incomplete volunteer review.');
  if (createPerson ? (!!payload.userId || payload.personId != null || !!payload.resultId)
    : (!nonempty(payload.userId) || !Number.isSafeInteger(payload.personId) || Number(payload.personId) <= 0))
    throw new Error('Select an explicit linked runner, or confirm creation of a new runner.');
  const normalized = { resultId: payload.resultId ?? null, editionId: payload.editionId, editionYear: payload.editionYear,
    userId: payload.userId ?? null, personId: payload.personId ?? null, firstName: payload.firstName.trim(),
    lastName: payload.lastName.trim(), displayName: payload.displayName?.trim() || null,
    email: payload.email?.trim().toLowerCase() || null, yearOfBirth: payload.yearOfBirth ?? null,
    gender: payload.gender ?? '', representing: Array.isArray(payload.representing)
      ? payload.representing.map(v => v.trim()).filter(Boolean) : payload.representing?.trim() ? [payload.representing.trim()] : [],
    createPerson };
  const bytes = await cryptoApi.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(normalized)));
  const payloadDigest = Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
  const key = storageKey(actorId, payload.editionId), raw = storage.getItem(key);
  let pending: PendingOperation;
  if (raw !== null) {
    try { pending = JSON.parse(raw); } catch { throw new Error('Pending volunteer operation needs reconciliation.'); }
    if (pending.version !== 1 || !/^[a-zA-Z0-9_-]{8,128}$/.test(pending.operationKey)
      || pending.payloadDigest !== payloadDigest)
      throw new Error('An earlier save is unresolved. Retry the same details or reconcile its result before starting another save.');
  } else {
    pending = { version: 1, operationKey: cryptoApi.randomUUID(), payloadDigest };
    storage.setItem(key, JSON.stringify(pending)); // Failure stops before a network call.
    if (storage.getItem(key) !== JSON.stringify(pending)) throw new Error('Cannot retain a safe retry key.');
  }
  return { ...normalized, operationKey: pending.operationKey };
}

export function acknowledgeVolunteerOperation(payload: MOVolunteerPayload, actorId: string,
  result: ManageMOVolunteerResult, storage: StorageLike = sessionStorage): void {
  if (!nonempty(result.userId) || !nonempty(result.resultId) || !Number.isSafeInteger(result.personId)
    || Number(result.personId) <= 0 || (payload.userId && result.userId !== payload.userId)
    || (payload.personId != null && result.personId !== payload.personId)
    || (payload.resultId && result.resultId !== payload.resultId))
    throw new Error('Save response needs reconciliation; retry key has been retained.');
  const key = storageKey(actorId, payload.editionId), raw = storage.getItem(key);
  if (!raw || JSON.parse(raw).operationKey !== payload.operationKey)
    throw new Error('Save acknowledgement does not match the pending operation.');
  storage.removeItem(key);
}
